import { useState, useRef, useEffect, useCallback, KeyboardEvent } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { searchFrenchCities } from "../../lib/frenchCities";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";

interface CityAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
}

export default function CityAutocomplete({
    value,
    onChange,
    placeholder = "Ville",
    className = "",
}: CityAutocompleteProps) {
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(isOpen);

    const updateSuggestions = useCallback((query: string) => {
        if (query.length < 1) {
            setSuggestions([]);
            setIsOpen(false);
            return;
        }
        const results = searchFrenchCities(query, 8);
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setActiveIndex(-1);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        onChange(val);
        updateSuggestions(val);
    };

    const selectCity = (city: string) => {
        onChange(city);
        setSuggestions([]);
        setIsOpen(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => Math.max(prev - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && suggestions[activeIndex]) {
                selectCity(suggestions[activeIndex]);
            } else {
                setIsOpen(false);
            }
        } else if (e.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
        }
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Highlight matching part of city name
    const highlightMatch = (city: string, query: string) => {
        if (!query) return <span>{city}</span>;
        const normalQuery = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const normalCity = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const idx = normalCity.indexOf(normalQuery);
        if (idx === -1) return <span>{city}</span>;
        return (
            <>
                <span>{city.slice(0, idx)}</span>
                <span className="font-semibold text-blue-600">{city.slice(idx, idx + query.length)}</span>
                <span>{city.slice(idx + query.length)}</span>
            </>
        );
    };

    return (
        <div ref={containerRef} className={`relative flex items-center ${className}`}>
            <MapPinIcon className="w-4 h-4 text-ink-faint absolute left-2 z-10 pointer-events-none" />
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => value.length >= 1 && updateSuggestions(value)}
                placeholder={placeholder}
                autoComplete="off"
                className="h-8 pl-7 pr-2.5 text-xs bg-surface border border-border rounded-lg hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent w-40 transition-colors duration-150"
            />
            {isOpen && suggestions.length > 0 && (
                <div ref={dropdownAlign.ref} className={`absolute top-full mt-1 ${dropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-48 py-1 overflow-hidden`}>
                    {suggestions.map((city, index) => (
                        <button
                            key={city}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                selectCity(city);
                            }}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors duration-150 ${
                                index === activeIndex
                                    ? "bg-info-subtle text-info"
                                    : "text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            <MapPinIcon className="w-3 h-3 flex-shrink-0 text-ink-faint" />
                            <span>{highlightMatch(city, value)}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
